import { IUserInfo } from 'app/interfaces/IAuthenticationContext';
import { IAuthProvider } from 'app/interfaces/IAuthProvider';
import { ILogger } from 'app/logger/ILogger';
import { GraphModel } from 'app/model/graphmodel';
import { GlobalVariables } from '../globals';
import { UrlInformationBaseService } from '../url-information-base.service';
import { AuthConstants } from './auth-constants';
import Base64 from 'base64-js';
import { filter, take, concatMap } from 'rxjs/operators';
import { stringIsNullOrEmpty, GlobalVariables as ShellGlobalVariables } from 'shell-models-lib';
import { IRoleAssginment } from 'app/interfaces/IRoleAssignment';
import {
    Http,
    Response,
    ResponseContentType,
    Headers
} from 'app/services/http';
import PQueue from 'p-queue';
import { pageLoadProperties } from 'app/logger/logger.a365-pageload-event';
import { GrantAuthDialogService } from './grant-auth-dialog/grant-auth-dialog.service';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { productSettings } from 'environments/environment';
import { AuthenticationResult, Configuration, PopupRequest, RedirectRequest, SilentRequest, PublicClientApplication, InteractionRequiredAuthError, BrowserAuthOptions } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-common';
import { sessionStorageGet, sessionStorageSet } from 'app/shared/utils/window.util';
import { BehaviorSubject } from 'rxjs';
import { EventRow } from 'app/logger/logger.event-row';
import { loggerConstants } from 'app/logger/logger.constants';

const baseElement: HTMLElement = document.getElementsByTagName('base')[0];

export class Msal2AuthProvider implements IAuthProvider {
    public static readonly className = 'Msal2AuthProvider';
    public static readonly msalSystemLoggingName = 'Msal2SystemLogging';
    private static readonly longRunningAcquireTokenDurationMs = 30 * 1000;
    private static readonly aadstsRegex = /(AADSTS\d+)/;
    private static readonly invalidResourceErrorCode = 'invalid_resource';
    private static readonly browserConstants = {
        POPUP_WIDTH: 483,
        POPUP_HEIGHT: 600,
        POPUP_NAME_PREFIX: 'msal'
    };
    public userPhotoData$ = new BehaviorSubject<string>('');
    public fetchTenantAsync: Promise<string>;
    public readonly fetchUserRoleAsync: Promise<IRoleAssginment[]>;
    public fetchUserRoleResolver: (value: IRoleAssginment[]) => void;
    public isNavigatingBetweenInternalAndExternal = false;

    private _promiseQueue = new PQueue({ concurrency: 1 });
    private _msalInstance: PublicClientApplication;
    private _msalAccount: AccountInfo;
    private _handleRedirectPromise: Promise<void>;
    private _getTokenPopupInProgress?: Promise<void>;

    constructor(
        private readonly _httpClient: Http,
        private readonly _logger: ILogger,
        private readonly _urlInfo: UrlInformationBaseService,
        private readonly _grantAuthDialogService: GrantAuthDialogService,
        private readonly _tenantId: string,
        private readonly _reloginWhenTenantIdMismatched: boolean,
        private readonly _authOptions?: Pick<BrowserAuthOptions, 'cloudDiscoveryMetadata'>
    ) {
        this.fetchUserRoleAsync = new Promise((resolve) => {
            this.fetchUserRoleResolver = resolve;
        });
        const urlInfoMain = this._urlInfo;
        // wait for UrlInformationService get the subscriptionID, as we don't allow customer change Tenant, just use the first subId.
        this.fetchTenantAsync = !this._tenantId ? urlInfoMain.subId$
            .pipe(
                filter((subId) => subId !== undefined),
                take(1),
                concatMap((subId) => {
                    this._logger.log(Msal2AuthProvider.className, 'fetchTenantAsync - unexpected usage of fetchTenantAsync', { subId });
                    return this.getTenant(subId, true);
                })).toPromise() : Promise.resolve(this._tenantId);

        let baseHref = '/';
        if (baseElement) {
            // Applies when we have customized base href setting.
            const initialHref = baseElement.getAttribute('href');
            const hrefSuffix = initialHref === '/' ? '' : initialHref.endsWith('/') ? 'index.html' : '/index.html';
            baseHref = initialHref + hrefSuffix;
        }
        this._msalInstance = new PublicClientApplication(
            this._createConfig(baseHref)
        );
        // process the hash and cache tokens in local/session storage.
        this._handleRedirectPromise = this._msalInstance.handleRedirectPromise().then((authResult: AuthenticationResult) => {
            // logged in, starts to set _msalAccount and cached user
            let accountObj: AccountInfo = authResult?.account || undefined;
            // authResult is not null only when the login redirection happens, and it's null when the user's logged in.
            if (accountObj == null) {
                const currentAccounts = this._msalInstance.getAllAccounts();
                const requiredTenant = this._tenantId;
                if (currentAccounts == null || currentAccounts.length === 0) {
                    this._logger.warn(Msal2AuthProvider.className, 'getAccount - no accounts detected.');
                } else if (currentAccounts.length === 1) {
                    this._logger.warn(Msal2AuthProvider.className, 'getAccount - one account is detected, select it');
                    accountObj = currentAccounts[0];
                    const shouldRelogin = this._reloginWhenTenantIdMismatched && requiredTenant && accountObj?.tenantId &&
                        accountObj.tenantId.toLowerCase() !== requiredTenant.toLowerCase();
                    if (shouldRelogin) {
                        this._logger.warn(Msal2AuthProvider.className, 'getAccount - tenant id is mismatched, re-logging in');
                        accountObj = null;
                    }
                } else {
                    this._logger.warn(Msal2AuthProvider.className, 'getAccount - multiple accounts detected, choose based on CurrentSessionLocalAccountId');
                    // CurrentSessionLocalAccountId records the selected account in the current session
                    // Note that this is not accurate in cross tenant ADO access scenarios
                    const currentAccId = sessionStorageGet('CurrentSessionLocalAccountId');
                    const accountsByTenant = currentAccounts.filter((acc) => acc.tenantId === requiredTenant);
                    // checking this in case that user refreshes page in the same session after switching to a new account, and we'd be able to get the selected account back
                    accountObj = accountsByTenant.find((acc) => acc.localAccountId === currentAccId) || accountsByTenant[0] || currentAccounts[0];
                    const shouldRelogin = this._reloginWhenTenantIdMismatched && requiredTenant && accountObj?.tenantId &&
                        accountObj.tenantId.toLowerCase() !== requiredTenant.toLowerCase();
                    if (shouldRelogin) {
                        this._logger.warn(Msal2AuthProvider.className, 'getAccount - tenant id is mismatched, re-logging in');
                        accountObj = null;
                    }
                }
            }

            // keep CurrentSessionLocalAccountId up to date
            sessionStorageSet('CurrentSessionLocalAccountId', accountObj?.localAccountId || undefined);
            this._msalAccount = accountObj;
            // Only the main app provider enforces tenant matching; Git-scoped providers must not log factory access.
            if (this._msalAccount && this._reloginWhenTenantIdMismatched) {
                const homeTenant = this._getHomeTenantId();
                const guestStatus = !homeTenant || !this._tenantId ? 'unknown'
                    : homeTenant.toLowerCase() === this._tenantId.toLowerCase() ? 'home' : 'guest';
                this._logger.logEvent(new EventRow(loggerConstants.Event_ResourceAccessIdentity, {
                    product: __IS_A365__ ? 'A365' : 'ADF', guestStatus,
                    homeTenantId: homeTenant, tenantId: this._tenantId
                }));
            }
            if (this._msalAccount && this._isMSInternalUser() && GlobalVariables.redirectMicrosoftTenant) {
                ShellGlobalVariables.isMicrosoftInternal = true;

            }
            const specifiedUserTenant = /[\?&]specifiedUserTenant=([^&]+)($|&)/.exec(window.location.search)?.[1];
            if (specifiedUserTenant) {
                this._logger.log(Msal2AuthProvider.className, 'specifiedUserTenant is not null', {
                    specifiedUserTenant
                });
            }
        }).catch((error) => {
            const { errorCode, message, subError } = error || { errorCode: undefined, message: undefined, subError: undefined };
            const aadstsErrorCode = (Msal2AuthProvider.aadstsRegex.exec(message) || ['n/a'])[0];

            this._logger.warn(Msal2AuthProvider.className, 'handleRedirectPromise - failed', {
                errorCode: String(errorCode),
                subError: String(subError),
                aadstsErrorCode
            });
        });
    }

    public switchAccount(): void {
        this._logger.log(Msal2AuthProvider.className, 'switchAccount clicked');
        const request: RedirectRequest = {
            redirectStartPage: window.location.href,
            scopes: ['openid'],
            prompt: 'select_account'
        };
        this._msalInstance.loginRedirect(request);
    }

    public getCachedUser(): IUserInfo {
        const account = this._msalAccount;
        if (!account) {
            return undefined;
        }

        return {
            userName: account.username,
            profile: account.idTokenClaims
        };
    }

    public async isLoggedIn(): Promise<boolean> {
        // make ensure handleRedirectPromise has resolved before invoking any other MSAL method
        await this._handleRedirectPromise;
        return this._msalAccount != null;
    }

    public getCurrentUserTenant(): string {
        const account = this._msalAccount;
        return account ? account.tenantId : undefined;
    }

    public async isGuestUser(): Promise<boolean> {
        const resourceTenantId = await this.fetchTenantAsync;
        const homeTenantId = this._getHomeTenantId();
        return !!homeTenantId
            && !!resourceTenantId
            && homeTenantId.toLowerCase() !== resourceTenantId.toLowerCase();
    }

    public async fetchPhotoFromGraphAPI(user?: string): Promise<string> {
        this._logger.log(Msal2AuthProvider.className, 'start fetchPhotoFromGraphAPI', { callStack: new Error().stack });
        const apiStr = user ? `/v1.0/users/${user}/photo/$value` : '/v1.0/me/photo/$value';
        // "User.ReadBasic.All" stands as shorthand for "graph.microsoft.com/User.ReadBasic.All"
        const token = await this.getToken(['User.ReadBasic.All']);
        const headers = new Headers();
        headers.append('Authorization', `Bearer ${token}`);

        const responseType: ResponseContentType = ResponseContentType.ArrayBuffer;
        let arrayBuffer: ArrayBuffer;
        try {
            const response = await this._httpClient.get(`${AuthConstants.MSAL_GRAPH_ENDPOINT}${apiStr}`, { headers, responseType }).toPromise();
            arrayBuffer = response.arrayBuffer();
        } catch (err) {
            arrayBuffer = new ArrayBuffer(0); // Return empty ArrayBuffer when user photo does not exist.
        }

        this._logger.log(Msal2AuthProvider.className, 'finish fetchPhotoFromGraphAPI', { arrayBufferLength: arrayBuffer.byteLength + '', callStack: new Error().stack });
        if (arrayBuffer.byteLength === 0) {
            return '';
        }
        return 'data:image/jpeg;base64,' + Base64.fromByteArray(new Uint8Array(arrayBuffer));
    }

    public async fetchUserInfo(user: string): Promise<GraphModel.IAADObject> {
        const token = await this.getToken(['User.ReadBasic.All']);
        const headers = new Headers();
        headers.append('Authorization', `Bearer ${token}`);

        const response: Response = await this._httpClient.get(`${AuthConstants.MSAL_GRAPH_ENDPOINT}/v1.0/users/${user}`, { headers }).toPromise();
        return response.json();
    }

    public async getUserPhoto(): Promise<void> {
        const userPhotoUrl = await this.fetchPhotoFromGraphAPI();
        this._setUserPhotoData(userPhotoUrl);
    }

    public clearCache(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this._msalInstance as any).browserStorage?.clear?.();
    }

    public getUserID(): string {
        const cachedUser = this.getCachedUser();
        return cachedUser?.userName || '';
    }

    public getUserDisplayName(): string {
        const cachedUser = this.getCachedUser();
        return cachedUser?.profile?.name || '';
    }

    public getUserName(): string {
        return this.getUserID().split('@')[0];
    }

    public async getTenant(subId: string, setVariable: boolean = false): Promise<string | undefined> {
        if (subId && subId !== 'default') {
            try {
                // Follow https://github.com/MicrosoftDocs/azure-docs/blob/master/articles/azure-resource-manager/resource-manager-api-authentication.md
                // TenantId will be returned in the header of the response.
                await this._httpClient.get(`${AuthConstants.RESOURCE_MANAGER_ENDPOINT}subscriptions/${subId}`, {
                    params: { 'api-version': '2015-05-01' }
                }).toPromise();
            } catch (reason) {
                const wwwHeaderVal = reason.headers.get('www-authenticate');
                if (reason.status === 401 && !stringIsNullOrEmpty(wwwHeaderVal)) {
                    const authUrl: string = wwwHeaderVal.split(',')[0].split('=')[1];
                    // Fetch the tenant GUID ID and the length should be 36.
                    const tenantId: string = authUrl.substring(authUrl.lastIndexOf('/') + 1, authUrl.lastIndexOf('/') + 37);
                    if (setVariable) {
                        ShellGlobalVariables.isMicrosoftInternal = GlobalVariables.msInternalTenants.indexOf(tenantId) > -1;
                    }
                    return Promise.resolve(tenantId);
                }
            }
        }
        return Promise.resolve(undefined);
    }

    public handleLogin(): void {
        const loginHint = this._getLoginHint();
        this._logger.log(Msal2AuthProvider.className, 'loggedIn is false', { _authProvider: '2.x', callStack: new Error().stack });
        // start to redirect login
        this._logger.warn(Msal2AuthProvider.className, 'handleLogin - no account information. Pending redirect.');
        this.loginRedirect(loginHint);
    }

    public loginRedirect(loginHint?: string): void {
        this._logger.warn(Msal2AuthProvider.className, 'loginRedirect is triggered');
        const request: RedirectRequest = {
            // ensure that redirectStartPage doesn't contain hash fragment
            // to work around msal 2.0 issue https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4400
            redirectStartPage: window.location.href.split('#')[0],
            scopes: ['openid'],
            loginHint
        };
        this._msalInstance.loginRedirect(request);
    }

    public logOut(): void {
        this._msalInstance.logoutRedirect();
    }

    public async loginPopUp(tenantId?: string): Promise<AccountInfo> {
        this._logger.log(Msal2AuthProvider.className, 'loginPopUp clicked');
        const request: PopupRequest = {
            scopes: ['openid'],
            prompt: 'select_account'
        };

        request.authority = AuthConstants.AAD_ENDPOINT + (tenantId ? tenantId : 'organizations');

        const popupResult = await this._msalInstance.loginPopup(request);
        return popupResult.account;
    }

    public async logOutByAccountName(userName: string): Promise<void> {
        return this._msalInstance.logoutPopup({
            account: this._msalInstance.getAccountByUsername(userName)
        });
    }

    public async getAllAccounts(): Promise<AccountInfo[]> {
        return this._msalInstance.getAllAccounts();
    }

    public async getAllLoggedinUsers(): Promise<IUserInfo[]> {
        return (await this.getAllAccounts()).map((account) => ({
            userName: account.username,
            profile: account.idTokenClaims
        }));
    }

    public async getToken(authArea: string | string[], disableRedirection?: boolean, msalAccount?: AccountInfo, tenantId?: string, ignoreTokenFailure?: boolean): Promise<string> {
        const loggedIn = await this.isLoggedIn();
        if (!loggedIn) {
            // log and monitor in PROD before 'return' in this case
            this._logger.log(Msal2AuthProvider.className, 'getToken - unexpected getToken call before logging in', { tenantId, authArea: String(authArea), disableRedirection: String(disableRedirection), callStack: new Error().stack });
        }

        // priority order: tenantId > _tenantId > this._urlInfo.tenantId > this.fetchTenantAsync
        const tid = tenantId || this._tenantId || this._urlInfo.tenantId || await this.fetchTenantAsync;
        const request: SilentRequest = await this._generateAuthParams(authArea, tid, msalAccount);

        const getTokenStart = performance.now();
        const getQueuedPopups = () => this._promiseQueue.size + this._promiseQueue.pending;
        const maxAcquireTokenSilentAttempts = 5;

        for (let attempt = 0; attempt < maxAcquireTokenSilentAttempts; ++attempt) {
            const attemptStart = performance.now();
            try {
                let longRunning = false;
                const timerId = setTimeout(() => longRunning = true, Msal2AuthProvider.longRunningAcquireTokenDurationMs);
                const authResult = await this._msalInstance.acquireTokenSilent(request).finally(() => clearTimeout(timerId));

                if (!authResult.fromCache || longRunning) {
                    this._logger.log(Msal2AuthProvider.className, 'getToken - acquireTokenSilent succeeded', {
                        correlation: request.correlationId,
                        attempt: String(attempt),
                        authority: request.authority,
                        scopes: JSON.stringify(request.scopes),
                        disableRedirection: String(disableRedirection),
                        getTokenStart: String(getTokenStart),
                        attemptStart: String(attemptStart),
                        attemptEnd: String(performance.now()),
                        queuedPopups: String(getQueuedPopups()),
                        expiresOn: String(authResult.expiresOn.getTime()),
                        fromCache: String(authResult.fromCache),
                        forceRefresh: String(request.forceRefresh)
                    });
                }

                if (!authResult.fromCache) {
                    this._logAcquireTokenTimings(authArea);
                }

                return authResult.accessToken;
            } catch (e) {
                const { errorCode, message, subError } = e || { errorCode: undefined, message: undefined, subError: undefined };
                const aadstsErrorCode = (Msal2AuthProvider.aadstsRegex.exec(message) || ['n/a'])[0];

                this._logger.warn(Msal2AuthProvider.className, 'getToken - acquireTokenSilent failed', {
                    correlation: request.correlationId,
                    attempt: String(attempt),
                    authority: request.authority,
                    scopes: JSON.stringify(request.scopes),
                    disableRedirection: String(disableRedirection),
                    getTokenStart: String(getTokenStart),
                    attemptStart: String(attemptStart),
                    attemptEnd: String(performance.now()),
                    queuedPopups: String(getQueuedPopups()),
                    errorCode: String(errorCode),
                    subError: String(subError),
                    aadstsErrorCode,
                    forceRefresh: String(request.forceRefresh)
                });
                this._logAcquireTokenTimings(authArea, String(errorCode));

                if (ignoreTokenFailure) {
                    this._logger.warn(Msal2AuthProvider.className, 'getToken - ignoring token failure', { correlation: request.correlationId });
                    return '';
                }

                if (e instanceof InteractionRequiredAuthError || (errorCode === 'monitor_window_timeout')) {
                    if (attempt === maxAcquireTokenSilentAttempts - 1) {
                        this._logger.error(Msal2AuthProvider.className, 'getToken - Maximum acquireTokenSilent attempts reached. Fallback to acquireTokenPopup',
                            { correlation: request.correlationId });
                        return this._getTokenPopupPromise(request);
                    }

                    // If there is a popup in-progress, wait for its completion and then retry acquireTokenSlient.
                    if (this._getTokenPopupInProgress) {
                        await this._getTokenPopupInProgress;
                        continue;
                    }

                    // Otherwise, fetch token with popup.
                    const getTokenPopupResult = this._getTokenPopupPromise(request);
                    // The promise is only used for progress tracking, so result and exception are ignored.
                    this._getTokenPopupInProgress = getTokenPopupResult.then(() => { /*noop*/ }, () => { /*noop*/ });
                    this._getTokenPopupInProgress.then(() => { this._getTokenPopupInProgress = undefined; });
                    return getTokenPopupResult;
                } else if (errorCode === Msal2AuthProvider.invalidResourceErrorCode) {
                    this._logger.warn(Msal2AuthProvider.className, 'getToken - Invalid resource error hit', { correlation: request.correlationId });
                    throw e;
                } else if (!disableRedirection && !this.isNavigatingBetweenInternalAndExternal) {
                    // do not redirect if we're already in a navigation between internal and external portal
                    this._logger.warn(Msal2AuthProvider.className, 'getToken - Pending redirect', { correlation: request.correlationId });
                    this._msalInstance.acquireTokenRedirect(request);
                    throw e;
                } else {
                    this._logger.warn(Msal2AuthProvider.className, 'getToken - Unhandled error encountered', {
                        correlation: request.correlationId,
                        errorCode: String(errorCode),
                        subError: String(subError),
                        aadstsErrorCode
                    });
                    throw e;
                }
            }
        }

        this._logger.error(Msal2AuthProvider.className, 'getToken - Unreachable code encountered', { correlation: request.correlationId });
        throw new Error('getToken - Unreachable code encountered');
    }

    private _getHomeTenantId(): string | undefined {
        return /^[^.]+\.([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})$/i
            .exec(this._msalAccount?.homeAccountId || '')?.[1];
    }

    private _getLoginHint(): string {
        let loginHint;
        try {
            const hash = window.location.hash;
            if (hash) {
                const hashs = hash.substr(1).split('&');
                if (hashs) {
                    const loginHintPair = hashs.map((h) => h.split('=')).find((h) => h[0] === 'loginHint');
                    if (loginHintPair) {
                        loginHint = loginHintPair[1] || undefined;
                    }
                }
            }
        } catch (error) {
            // do nothing
        }
        return loginHint;
    }

    private _logAcquireTokenTimings(authArea: string | string[], errorCode?: string, subError?: string): void {
        if (__IS_A365__ && pageLoadProperties) {
            pageLoadProperties.tokenTimings = (pageLoadProperties.tokenTimings || []).concat({ authArea, errorCode, subError });
        }
    }

    private async _getTokenPopupPromise(authParams: PopupRequest): Promise<string> {
        await this._promiseQueue.onIdle();
        const authResult = await this._promiseQueue.add<AuthenticationResult>(() => {
            return new Promise(async (resolve, reject) => {
                try {
                    this._logger.warn(Msal2AuthProvider.className, 'getToken - Prompting for user credentials to get the token', { correlation: authParams.correlationId });
                    const authResult = await this._msalInstance.acquireTokenPopup(authParams);
                    resolve(authResult);
                } catch (e) {
                    this._logger.warn(Msal2AuthProvider.className, `getToken - cannot get token by popup, error code:  ${e && e.errorCode}`, { correlation: authParams.correlationId });
                    if (e.errorCode === 'popup_window_error') {
                        try {
                            this._logger.log(Msal2AuthProvider.className, 'getToken - before notify grant auth', { correlation: authParams.correlationId });
                            const authResult = await this._notifyGrantAuth(authParams);
                            resolve(authResult);
                        } catch (e) {
                            this._logger.warn(Msal2AuthProvider.className, `getToken - error when grant authentication: ${e.message}`, { correlation: authParams.correlationId });
                            reject(e);
                        }
                    } else {
                        this._logger.warn(Msal2AuthProvider.className, `getToken - other error when cannot open pop-up window:  ${e && e.errorCode}`, { correlation: authParams.correlationId });
                        reject(e);
                    }
                }
            });
        });
        return authResult.accessToken;
    }

    private async _generateAuthParams(authArea: string | string[], tenantId?: string, msalAccount?: AccountInfo): Promise<SilentRequest> {
        const correlationId = generateUUID();
        let scopes: string[] = [];
        if (typeof authArea === 'string') {
            authArea = authArea + '/.default';
            scopes = [authArea];
        } else {
            scopes = authArea;
        }
        const curAccount = msalAccount ?? this._msalAccount;
        // to work around issue: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/3687
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const shouldForceRefresh = msalAccount ? false : tenantId && (curAccount?.idTokenClaims as any)?.tid && (curAccount.idTokenClaims as any).tid.toLowerCase() !== tenantId.toLowerCase();
        if (shouldForceRefresh) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this._logger.log(Msal2AuthProvider.className, 'shouldForceRefresh is true', { correlation: correlationId, passedInTenantId: tenantId, accountTid: (curAccount.idTokenClaims as any).tid });
        }
        return {
            scopes,
            authority: tenantId != null ? AuthConstants.AAD_ENDPOINT + tenantId : undefined,
            forceRefresh: shouldForceRefresh,
            account: curAccount,
            correlationId
            // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/errors.md#block_iframe_reload
            // redirectUri: window.location.origin + '/accessToken.html' // this is to avoid block_iframe_reload error
        };
    }

    private _isMSInternalUser(): boolean {
        const user = this.getCachedUser();
        return user?.profile?.tid && GlobalVariables.msInternalTenants.indexOf(user.profile.tid) > -1;
    }

    private _createConfig(baseHref: string): Configuration {
        const msalConfig: Configuration = {
            auth: {
                clientId: productSettings.value.clientId,
                // login tenant priority: this._tenantId (data factory) > organizations(user's home tenant)
                authority: AuthConstants.AAD_ENDPOINT + (this._tenantId || 'organizations'),
                cloudDiscoveryMetadata: this._authOptions?.cloudDiscoveryMetadata,
                redirectUri: window.location.origin,
                postLogoutRedirectUri: window.location.origin + baseHref,
                navigateToLoginRequestUrl: true
            },
            cache: {
                storeAuthStateInCookie: false,
                cacheLocation: 'sessionStorage'
            },
            system: {
                // comment out to reduce logging burden
                // loggerOptions: {
                //     loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
                //         if (containsPii) {
                //             return;
                //         }
                //         switch (level) {
                //             case LogLevel.Error:
                //                 console.error(message);
                //                 return;
                //             case LogLevel.Info:
                //                 console.info(message);
                //                 return;
                //             case LogLevel.Verbose:
                //                 console.debug(message);
                //                 return;
                //             case LogLevel.Warning:
                //                 console.warn(message);
                //                 return;
                //         }
                //     },
                //     piiLoggingEnabled: false
                // },
                loadFrameTimeout: 30 * 1000
            }
        };
        return msalConfig;
    }

    private _notifyGrantAuth(request: PopupRequest): Promise<AuthenticationResult> {
        return new Promise((resolve, reject) => {
            this._grantAuthDialogService.openDialog(
                () => {
                    // This is for preventing pop-up block policy by browser.
                    // With user's consent, we open a popup dialog with the same name as MSAL is going to open, say to prepare an existing pop up for MSAL to navigate to auth enrpoint
                    // FIXME: popupName is constructed with the same way as PopupUtils.generatePopupName in MSAL 2.17.0
                    const popupName = `${Msal2AuthProvider.browserConstants.POPUP_NAME_PREFIX}.${productSettings.value.clientId}.${request.scopes.join('-')}.${request.authority}.${request.correlationId}`;
                    this._openSizedPopup('about:blank', popupName);
                    resolve(this._msalInstance.acquireTokenPopup(request));
                },
                () => {
                    reject(new Error('_notifyGrantAuth in MSAL 2.0: User denied grant authentication.'));
                }
            );
        });
    }

    private _openSizedPopup(urlNavigate: string, popupName: string): void {
        /**
         * adding winLeft and winTop to account for dual monitor
         * using screenLeft and screenTop for IE8 and earlier
         */
        const winLeft = window.screenLeft ? window.screenLeft : window.screenX;
        const winTop = window.screenTop ? window.screenTop : window.screenY;
        /**
         * window.innerWidth displays browser window"s height and width excluding toolbars
         * using document.documentElement.clientWidth for IE8 and earlier
         */
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        const left = Math.max(0, ((width / 2) - (Msal2AuthProvider.browserConstants.POPUP_WIDTH / 2)) + winLeft);
        const top = Math.max(0, ((height / 2) - (Msal2AuthProvider.browserConstants.POPUP_HEIGHT / 2)) + winTop);

        window.open(urlNavigate, popupName, `width=${Msal2AuthProvider.browserConstants.POPUP_WIDTH}, height=${Msal2AuthProvider.browserConstants.POPUP_HEIGHT}, top=${top}, left=${left}, scrollbars=yes`);
    }

    private _setUserPhotoData(v: string): void {
        this.userPhotoData$.next(v);
    }
}
